/**
 * Les loyers impayés de l'espace, tous contrats confondus (#396).
 *
 * Jusqu'ici les arriérés n'existaient qu'en cartes cockpit — une par contrat,
 * mêlées à tout le reste. Pour répondre à « qui me doit combien, et depuis
 * quand », il fallait ouvrir chaque contrat. Ce service rassemble.
 *
 * ## Pourquoi sous `core/` et plus sous `pages/apps/rent-arrears/`
 *
 * L'aside propriétaire affiche le nombre d'impayés en pastille, et
 * `core/navigation` ne peut pas importer une page (`check-layer-boundaries.js`).
 * Le service n'a jamais dépendu de la page — Firestore et `WorkspaceStore`
 * seulement — il descend donc d'une couche, sans changer d'API pour la page.
 *
 * ## Ce qu'il ne fait PAS, délibérément
 *
 * Il ne recalcule pas l'escalade. Les étages {J+7, J+14, J+21} sont lus par le
 * backend (`extractReminderPolicy`), qui décide du ton et du rang du rappel. Les
 * réimplémenter ici créerait une seconde source de vérité, et deux sources qui
 * divergent sur une relance de loyer, c'est un locataire relancé deux fois ou
 * pas du tout.
 *
 * Cette vue trie sur ce qu'elle peut établir seule et sans ambiguïté : le
 * nombre de jours de retard. Le ton reste l'affaire de la carte cockpit.
 */

import { Injectable, inject } from '@angular/core';
import { Firestore, collection, doc, getDoc, getDocs, query, where } from '@angular/fire/firestore';

import { WorkspaceStore } from '../../../shared/services/workspace.store';

/** Une échéance de loyer non réglée, avec de quoi la nommer à l'écran. */
export interface RentArrear {
  contractUID: string;
  paymentUID: string;
  /** Date d'échéance. */
  dueDate: Date;
  amount: number;
  currency: string;
  /** Jours de retard. Négatif = pas encore échue. */
  overdueDays: number;
  tenantName: string;
  propertyLabel: string;
}

/** Ce que la page affiche en tête. */
export interface ArrearsSummary {
  /** Échéances déjà dépassées. */
  overdue: RentArrear[];
  /** Échéances à venir dans les 30 jours. */
  upcoming: RentArrear[];
  /** Somme des échéances dépassées. */
  overdueTotal: number;
  currency: string;
}

/** Ce que la pastille de l'aside a besoin de savoir — rien de plus. */
export interface ArrearsCount {
  /** Échéances déjà dépassées. */
  count: number;
  /** Le retard le plus ancien, en jours (0 si aucun). */
  maxOverdueDays: number;
}

/** Une échéance retenue par le scan, avant enrichissement par les libellés. */
export interface OutstandingPayment {
  paymentUID: string;
  dueDate: Date;
  amount: number;
  overdueDays: number;
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;
/** Fenêtre « à échoir » : au-delà, ce n'est plus une échéance à surveiller. */
const UPCOMING_WINDOW_DAYS = 30;

/** Une valeur Firestore qui peut être un Timestamp, une chaîne ou une Date. */
function toDate(raw: unknown): Date | null {
  if (!raw) return null;
  if (raw instanceof Date) return isNaN(raw.getTime()) ? null : raw;
  if (typeof raw === 'object' && typeof (raw as { toDate?: unknown }).toDate === 'function') {
    return (raw as { toDate: () => Date }).toDate();
  }
  const d = new Date(raw as string);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Retenir, parmi les `items` d'un `plannedPayments/data`, ce qui reste à
 * encaisser : ni payé, ni sauté, ni résilié, encaissement seulement, et
 * échu ou à échoir dans les 30 jours.
 *
 * Pure et partagée : la page (`load`) et la pastille de l'aside (`countOverdue`)
 * doivent compter EXACTEMENT les mêmes lignes, sinon le chiffre de l'aside
 * contredit la page qu'il ouvre.
 *
 * @param items Les échéances brutes du document Firestore.
 * @param now   L'instant de référence, injecté pour rester testable.
 * @return Les échéances retenues, dans l'ordre du document.
 */
export function outstandingPayments(
  items: readonly Record<string, unknown>[],
  now: Date
): OutstandingPayment[] {
  const out: OutstandingPayment[] = [];
  for (const p of items) {
    if (p['paid'] === true || p['skipped'] === true || p['terminated'] === true) continue;
    if (p['isIncome'] === false) continue;
    const due = toDate(p['date']);
    if (!due) continue;

    const overdueDays = Math.floor((now.getTime() - due.getTime()) / MS_PER_DAY);
    // Au-delà de la fenêtre, une échéance n'est ni un retard ni une
    // urgence : l'afficher noierait ce qui l'est.
    if (overdueDays < -UPCOMING_WINDOW_DAYS) continue;

    out.push({
      paymentUID: String(p['paymentUID'] ?? ''),
      dueDate: due,
      amount: Number(p['amount']) || 0,
      overdueDays
    });
  }
  return out;
}

/** État du loyer du mois en cours pour un contrat (#645, C1). */
export interface MonthPaymentStatus {
  state: 'paid' | 'late' | 'due' | 'none';
  /** Montant concerné : impayé si en retard, attendu si à venir, payé sinon. */
  amount: number;
  /** Échéance de référence (la plus ancienne impayée, ou la prochaine). */
  date: Date | null;
  overdueDays: number;
}

function sameMonth(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
}

/**
 * Le loyer du mois en cours, d'après les échéances planifiées d'UN contrat.
 *
 * Mêmes exclusions que `outstandingPayments` (sautées, résiliées, dépenses).
 * Le retard l'emporte : un mois réglé à moitié n'est pas « payé ».
 */
export function monthPaymentStatus(items: readonly Record<string, unknown>[], now: Date): MonthPaymentStatus {
  let lateAmount = 0;
  // `as` : sinon TypeScript rétrécit à `null` et refuse les comparaisons de la boucle.
  let lateDate = null as Date | null;
  let dueAmount = 0;
  let dueDate = null as Date | null;
  let paidAmount = 0;
  let paidDate = null as Date | null;

  for (const p of items) {
    if (p['skipped'] === true || p['terminated'] === true || p['isIncome'] === false) continue;
    const due = toDate(p['date']);
    if (!due || !sameMonth(due, now)) continue;
    const amount = Number(p['amount']) || 0;
    if (p['paid'] === true) {
      paidAmount += amount;
      paidDate = paidDate && paidDate > due ? paidDate : due;
    } else if (due.getTime() < now.getTime()) {
      lateAmount += amount;
      lateDate = lateDate && lateDate < due ? lateDate : due;
    } else {
      dueAmount += amount;
      dueDate = dueDate && dueDate < due ? dueDate : due;
    }
  }

  if (lateDate) {
    return {
      state: 'late',
      amount: lateAmount,
      date: lateDate,
      overdueDays: Math.floor((now.getTime() - lateDate.getTime()) / MS_PER_DAY)
    };
  }
  if (dueDate) return { state: 'due', amount: dueAmount, date: dueDate, overdueDays: 0 };
  if (paidDate) return { state: 'paid', amount: paidAmount, date: paidDate, overdueDays: 0 };
  return { state: 'none', amount: 0, date: null, overdueDays: 0 };
}

/** Ce qui est dû, encaissé et en retard sur une période (#645, FD1/FD2). */
export interface PeriodCollection {
  /** Échéances de la période, réglées ou non. */
  expected: number;
  /** Échéances de la période marquées payées. */
  collected: number;
  /** Échéances de la période échues et non réglées. */
  overdue: number;
}

/**
 * Additionner les échéances de loyer d'une période `[start, end[`, mêmes
 * exclusions que `outstandingPayments` (sautées, résiliées, dépenses).
 *
 * `propertyUIDs` restreint au périmètre choisi ; `null` = tout le portefeuille.
 * Seuls les baux ACTIFS sont lus : une période passée ne compte donc pas les
 * baux terminés depuis — la limite est dite à l'écran.
 */
export function collectionForPeriod(
  contracts: readonly { propertyUID: string; items: readonly Record<string, unknown>[] }[],
  start: Date,
  end: Date,
  now: Date,
  propertyUIDs: ReadonlySet<string> | null = null
): PeriodCollection {
  const out: PeriodCollection = { expected: 0, collected: 0, overdue: 0 };
  for (const c of contracts) {
    if (propertyUIDs && !propertyUIDs.has(c.propertyUID)) continue;
    for (const p of c.items) {
      if (p['skipped'] === true || p['terminated'] === true || p['isIncome'] === false) continue;
      const due = toDate(p['date']);
      if (!due || due < start || due >= end) continue;
      const amount = Number(p['amount']) || 0;
      out.expected += amount;
      if (p['paid'] === true) out.collected += amount;
      else if (due < now) out.overdue += amount;
    }
  }
  return out;
}

/** Le retard cumulé d'un locataire, tous baux actifs confondus (#645, T2). */
export interface TenantBalance {
  /** Somme des échéances dépassées. */
  overdueAmount: number;
  /** Le retard le plus ancien, en jours. */
  maxOverdueDays: number;
  currency: string;
}

/**
 * Additionner les retards par locataire. Les échéances pas encore échues
 * (`overdueDays <= 0`) ne comptent pas : un solde dit ce qui est dû, pas ce
 * qui le sera.
 */
export function balancesByTenant(
  contracts: readonly { tenantUID: string; currency: string; payments: readonly OutstandingPayment[] }[]
): Map<string, TenantBalance> {
  return balancesBy(contracts, c => c.tenantUID);
}

/** Le même retard, rangé par bien (#645, P3 — alerte « loyer en retard »). */
export function balancesByProperty(
  contracts: readonly { propertyUID: string; currency: string; payments: readonly OutstandingPayment[] }[]
): Map<string, TenantBalance> {
  return balancesBy(contracts, c => c.propertyUID);
}

function balancesBy<C extends { currency: string; payments: readonly OutstandingPayment[] }>(
  contracts: readonly C[],
  keyOf: (c: C) => string
): Map<string, TenantBalance> {
  const out = new Map<string, TenantBalance>();
  for (const c of contracts) {
    const key = keyOf(c);
    if (!key) continue;
    for (const p of c.payments) {
      if (p.overdueDays <= 0) continue;
      const cur = out.get(key) ?? { overdueAmount: 0, maxOverdueDays: 0, currency: c.currency || 'EUR' };
      cur.overdueAmount += p.amount;
      cur.maxOverdueDays = Math.max(cur.maxOverdueDays, p.overdueDays);
      out.set(key, cur);
    }
  }
  return out;
}

/** Durée de vie du scan partagé entre la liste des contrats et celle des locataires. */
const SCAN_TTL_MS = 60_000;

interface ScannedContract {
  contractUID: string;
  tenantUID: string;
  propertyUID: string;
  currency: string;
  payments: OutstandingPayment[];
  items: Record<string, unknown>[];
}

@Injectable({ providedIn: 'root' })
export class RentArrearsService {
  /**
   * Le dernier scan complet, gardé une minute : passer des contrats aux
   * locataires ne relit pas une échéance par bail une seconde fois.
   */
  private scanCache: { ws: string; at: number; result: Promise<ScannedContract[]> } | null = null;
  private readonly db = inject(Firestore);
  private readonly workspace = inject(WorkspaceStore);

  private get workspaceId(): string {
    return this.workspace.currentWorkspaceId() ?? '';
  }

  /**
   * Rassembler les impayés de l'espace.
   *
   * Locataires et biens sont chargés en DEUX lectures, pas deux par contrat :
   * un espace de trente baux ferait soixante allers-retours pour afficher des
   * noms, et la page mettrait plusieurs secondes à s'ouvrir pour rien.
   *
   * @param now L'instant de référence, injecté pour rester testable.
   * @return Les impayés et les échéances proches, triés du plus ancien retard.
   */
  async load(now: Date = new Date()): Promise<ArrearsSummary> {
    const ws = this.workspaceId;
    if (!ws) return { overdue: [], upcoming: [], overdueTotal: 0, currency: 'EUR' };

    const [tenants, properties] = await Promise.all([
      // `tenantName` d'abord : c'est le champ que porte réellement la base.
      // Les autres sont des replis pour les documents anciens.
      this.libelles(`workspaces/${ws}/tenants`, ['tenantName', 'companyName', 'fullName', 'name', 'displayName']),
      this.libelles(`workspaces/${ws}/properties`, ['name', 'title', 'label']),
    ]);

    const overdue: RentArrear[] = [];
    const upcoming: RentArrear[] = [];
    let currency = 'EUR';

    for await (const contract of this.scanActiveContracts(ws, now)) {
      currency = contract.currency || currency;
      for (const p of contract.payments) {
        const ligne: RentArrear = {
          contractUID: contract.contractUID,
          paymentUID: p.paymentUID,
          dueDate: p.dueDate,
          amount: p.amount,
          currency: contract.currency || 'EUR',
          overdueDays: p.overdueDays,
          tenantName: tenants.get(contract.tenantUID) ?? '—',
          propertyLabel: properties.get(contract.propertyUID) ?? '—',
        };
        if (p.overdueDays > 0) overdue.push(ligne);
        else upcoming.push(ligne);
      }
    }

    // Le plus ancien retard en premier : c'est celui qui coûte le plus cher.
    overdue.sort((a, b) => b.overdueDays - a.overdueDays);
    // Les à-échoir dans l'ordre où ils arrivent.
    upcoming.sort((a, b) => a.dueDate.getTime() - b.dueDate.getTime());

    return {
      overdue,
      upcoming,
      overdueTotal: overdue.reduce((s, l) => s + l.amount, 0),
      currency,
    };
  }

  /**
   * Compter les impayés pour la pastille de l'aside — même scan que `load`,
   * sans les deux lectures de libellés dont une pastille n'a que faire.
   *
   * Reste N+1 lectures (un `plannedPayments/data` par contrat actif) : à
   * l'appelant de ne pas le rejouer à chaque navigation.
   */
  async countOverdue(now: Date = new Date()): Promise<ArrearsCount> {
    const ws = this.workspaceId;
    if (!ws) return { count: 0, maxOverdueDays: 0 };

    let count = 0;
    let maxOverdueDays = 0;
    for await (const contract of this.scanActiveContracts(ws, now)) {
      for (const p of contract.payments) {
        if (p.overdueDays <= 0) continue;
        count++;
        maxOverdueDays = Math.max(maxOverdueDays, p.overdueDays);
      }
    }
    return { count, maxOverdueDays };
  }

  /**
   * Le loyer du mois de chaque contrat actif (#645, C1) — même scan que les
   * impayés, une lecture par contrat.
   */
  async monthStatusByContract(now: Date = new Date()): Promise<Map<string, MonthPaymentStatus>> {
    const out = new Map<string, MonthPaymentStatus>();
    for (const contract of await this.scanAll(now)) {
      out.set(contract.contractUID, monthPaymentStatus(contract.items, now));
    }
    return out;
  }

  /** Le retard de chaque locataire (#645, T2 — colonne Solde). */
  async balanceByTenant(now: Date = new Date()): Promise<Map<string, TenantBalance>> {
    return balancesByTenant(await this.scanAll(now));
  }

  /**
   * Les baux actifs et leurs échéances, tels que lus par le scan partagé
   * (#645, FD1 — la vue financière les découpe par période).
   */
  async scannedContracts(now: Date = new Date()): Promise<readonly ScannedContract[]> {
    return this.scanAll(now);
  }

  /** Le retard de chaque bien (#645, P3). */
  async balanceByProperty(now: Date = new Date()): Promise<Map<string, TenantBalance>> {
    return balancesByProperty(await this.scanAll(now));
  }

  /**
   * Le scan complet, partagé une minute entre les listes. `now` sert au
   * premier appel ; un retard calculé à une minute près reste juste au jour.
   */
  private scanAll(now: Date): Promise<ScannedContract[]> {
    const ws = this.workspaceId;
    if (!ws) return Promise.resolve([]);
    const fresh = this.scanCache && this.scanCache.ws === ws && Date.now() - this.scanCache.at < SCAN_TTL_MS;
    if (!fresh) {
      const result = (async () => {
        const all: ScannedContract[] = [];
        for await (const contract of this.scanActiveContracts(ws, now)) all.push(contract);
        return all;
      })();
      // Un échec ne reste pas en cache : le prochain appel relit.
      result.catch(() => {
        if (this.scanCache?.result === result) this.scanCache = null;
      });
      this.scanCache = { ws, at: Date.now(), result };
    }
    return this.scanCache!.result;
  }

  /**
   * Parcourir les contrats actifs et leurs échéances retenues, un contrat à la
   * fois. Le `plannedPayments/data` est un sous-document : une lecture par
   * contrat, il n'existe pas de requête qui les ramène d'un coup.
   */
  private async *scanActiveContracts(ws: string, now: Date): AsyncGenerator<ScannedContract> {
    const contractsSnap = await getDocs(
      query(collection(this.db, `workspaces/${ws}/contracts`), where('status', '==', 'Active')),
    );

    for (const c of contractsSnap.docs) {
      const data = c.data() as Record<string, unknown>;
      const fin = (data['contractFinancials'] ?? {}) as Record<string, unknown>;

      const ppSnap = await getDoc(
        doc(this.db, `workspaces/${ws}/contracts/${c.id}/plannedPayments/data`),
      );
      if (!ppSnap.exists()) continue;
      const items = ((ppSnap.data() as { items?: Record<string, unknown>[] }).items ?? []);

      yield {
        contractUID: c.id,
        tenantUID: String(data['tenantUID'] ?? ''),
        propertyUID: String(data['propertyUID'] ?? ''),
        currency: (fin['currency'] as string) || '',
        payments: outstandingPayments(items, now),
        items,
      };
    }
  }

  /**
   * Charger une collection entière et en tirer un libellé par document.
   *
   * Les modèles de données ne sont pas unifiés (dette gelée, cf. CLAUDE.md) :
   * un locataire peut porter `fullName`, `name` ou `displayName` selon son
   * ancienneté. On prend le premier champ non vide plutôt que d'en imposer un.
   *
   * @param chemin La collection à lire.
   * @param champs Les noms de champ à essayer, dans l'ordre.
   * @return id → libellé.
   */
  private async libelles(chemin: string, champs: string[]): Promise<Map<string, string>> {
    const out = new Map<string, string>();
    try {
      const snap = await getDocs(collection(this.db, chemin));
      for (const d of snap.docs) {
        const data = d.data() as Record<string, unknown>;
        for (const f of champs) {
          const v = data[f];
          if (typeof v === 'string' && v.trim()) {
            out.set(d.id, v.trim());
            break;
          }
        }
      }
    } catch {
      // Un nom manquant dégrade l'affichage en « — ». Faire échouer toute la
      // page parce qu'un libellé n'a pas pu être lu serait disproportionné.
    }
    return out;
  }
}
